import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  FormControl,
  FormGroup,
  ControlLabel,
  Form,
  HelpBlock,
} from 'react-bootstrap';
import { Button as AntButton, Modal } from 'antd';
import {
  EditOutlined,
  DeleteFilled,
  PlusCircleFilled,
} from '@ant-design/icons';
import {
  LHSPlot,
  LCSPlot,
  LCFPlot,
  ELFPlot,
  CVGPlot,
  SCFPlot,
} from 'components/plots';
import moment from 'moment';

import {
  deleteFullReportMetadata,
  setFullReportMetadataItems,
  setReportData,
} from 'redux/actions';

const metadataOptions = [
  { label: '', value: 'Select an item to add' },
  { label: 'Manufacturer', value: 'manufacturer' },
  { label: 'Catalog Number or Model', value: 'model' },
  { label: 'Description', value: 'description' },
  { label: 'Document Creator', value: 'creator' },
  { label: 'Unique Identifier', value: 'identifier' },
  { label: 'Measurement', value: 'measurement' },
  { label: 'Laboratory', value: 'laboratory' },
  { label: 'Report Number', value: 'report_number' },
  { label: 'Report Date', value: 'report_date' },
  { label: 'Creation Date', value: 'creation_date' },
  { label: 'Comments', value: 'comments' },
  { label: 'Spectral Quantity', value: 'spectral_quantity' },
];

export const Full = ({
  reportData,
  showMetadataActions,
  active = 'Full',
  downloading,
}) => {
  const dispatch = useDispatch();

  const fullReportMetadataItems = useSelector(
    (state) => state.dataReducers.fullReportMetadataItems
  );
  const metadata = reportData.metadata;
  const Numerical_Results = reportData.tm30.Numerical_Results;
  const input = reportData.tm30.input;
  const location = window.location;
  const [addNewModal, setAddNewModal] = useState(false);
  const [editExisting, setEditExisting] = useState(false);
  const [newMetaItem, setNewMetaItem] = useState('');
  const [newMetaItemLabel, setNewMetaItemLabel] = useState('');
  const [newMetaItemValue, setNewMetaItemValue] = useState('');
  const [duplicateMetaData, setDuplicateMetaData] = useState(null);
  const [fullReportMetadata, setFullReportMetadata] = useState([]);

  useEffect(() => {
    let updatedFullReportMetadata = [];
    fullReportMetadataItems.forEach((item) => {
      const itemLabel = metadataOptions.find(
        (option) => option.value === item
      ).label;
      updatedFullReportMetadata.push({
        label: itemLabel,
        value: metadata && metadata[item],
      });
    });
    setFullReportMetadata(updatedFullReportMetadata);
  }, [metadata, fullReportMetadataItems]);

  useEffect(() => {
    // Getting corresponding label when a new meta item is selected
    let targetIndex;
    metadataOptions.forEach((option, index) => {
      if (option.value === newMetaItem) {
        targetIndex = index;
      }
    });
    const fullReportMetaDataLabels = fullReportMetadata.map(
      (data) => data.label
    );
    setNewMetaItemLabel(metadataOptions[targetIndex]?.label);

    if (
      !editExisting &&
      fullReportMetaDataLabels.includes(metadataOptions[targetIndex]?.label)
    ) {
      setDuplicateMetaData('error');
    } else {
      setDuplicateMetaData(null);
    }
  }, [
    newMetaItem,
    setDuplicateMetaData,
    setNewMetaItemLabel,
    active,
    dispatch,
    fullReportMetadata,
    addNewModal,
  ]);

  const deleteMetadata = (id) => {
    dispatch(deleteFullReportMetadata(id));
  };

  const addNewMetaData = () => {
    if (editExisting) {
      // Update meta data
      const updatedMetaData = {
        ...metadata,
        [newMetaItem]: newMetaItemValue,
      };
      const updatedReportData = {
        ...reportData,
        metadata: updatedMetaData,
      };
      dispatch(setReportData(updatedReportData));
    } else {
      dispatch(setFullReportMetadataItems(newMetaItem));
      // Also update meta data
      const updatedMetaData = {
        ...metadata,
        [newMetaItem]:
          newMetaItemValue !== '' ? newMetaItemValue : metadata[newMetaItem],
      };
      const updatedReportData = {
        ...reportData,
        metadata: updatedMetaData,
      };
      dispatch(setReportData(updatedReportData));
    }

    setAddNewModal(false);
    setNewMetaItem('');
    setNewMetaItemValue('');
  };

  const handleMetaValueChange = (event) => {
    setNewMetaItemValue(event.target.value);
  };

  const addNewModalContainer = (
    <Modal
      title='Add/Edit new metadata'
      visible={addNewModal}
      onCancel={() => setAddNewModal(false)}
      footer={[
        <AntButton key='close' onClick={() => setAddNewModal(false)}>
          Close
        </AntButton>,
        <AntButton
          disabled={duplicateMetaData === 'error'}
          key='add'
          type='primary'
          onClick={() => addNewMetaData()}
        >
          Save
        </AntButton>,
      ]}
    >
      <>
        {!editExisting && (
          <FormGroup validationState={duplicateMetaData}>
            <ControlLabel>Select a meta item to add</ControlLabel>
            <FormControl
              placeholder='Choose a meta item'
              componentClass='select'
              value={newMetaItem}
              onChange={(event) => setNewMetaItem(event.target.value)}
            >
              {metadataOptions.map((option, i) => (
                <option key={i} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FormControl>
            {duplicateMetaData === 'error' && (
              <HelpBlock style={{ fontSize: '14px' }}>
                Selected item already exists
              </HelpBlock>
            )}
          </FormGroup>
        )}

        {((newMetaItem &&
          metadata[newMetaItem] === undefined &&
          duplicateMetaData !== 'error') ||
          editExisting) && (
          <FormGroup
            validationState={
              newMetaItemValue === '' && !editExisting ? 'error' : null
            }
          >
            <ControlLabel>{newMetaItemLabel}</ControlLabel>
            <FormControl
              type='text'
              value={newMetaItemValue}
              onChange={handleMetaValueChange}
            />
            {newMetaItemValue === '' && !editExisting && (
              <HelpBlock style={{ fontSize: '14px' }}>
                {newMetaItemLabel} field is empty. You can enter something or
                proceed anyways{' '}
              </HelpBlock>
            )}
          </FormGroup>
        )}
      </>
    </Modal>
  );

  const MetaDataForm = ({ showMetadataActions = true }) => {
    const metadataFields = (
      <Form
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          marginTop: '1rem',
          maxWidth: '110rem',
          marginLeft: showMetadataActions ? '6px' : '0px',
        }}
      >
        {fullReportMetadata?.map((data, index) => {
          return (
            <FormGroup
              key={index}
              style={{ marginRight: '2rem', width: '24rem' }}
            >
              <ControlLabel>{data.label}:</ControlLabel>
              {showMetadataActions && (
                <EditOutlined
                  title='Edit'
                  style={{
                    margin: '0 6px',
                  }}
                  onClick={() => {
                    setAddNewModal(true);
                    setEditExisting(true);
                    const targetMetaTag = metadataOptions.find(
                      (option) => data.label === option.label
                    );
                    setNewMetaItem(targetMetaTag.value);
                    setNewMetaItemValue(data.value);
                  }}
                />
              )}
              {showMetadataActions && (
                <DeleteFilled
                  title='Delete'
                  onClick={() => deleteMetadata(index)}
                />
              )}
              <FormControl
                style={{ cursor: 'auto' }}
                title={data.value}
                disabled
                type='text'
                value={data.value}
              />
            </FormGroup>
          );
        })}
      </Form>
    );
    return (
      <div>
        {metadataFields}
        {showMetadataActions && (
          <AntButton
            prefix={<PlusCircleFilled />}
            type='primary'
            style={{
              marginBottom: '1rem',
              marginLeft: '6px',
            }}
            onClick={() => {
              setAddNewModal(true);
              setEditExisting(false);
            }}
          >
            Add new metadata
          </AntButton>
        )}
      </div>
    );
  };

  const reportColumnWidths = {
    xs: downloading ? 6 : 12,
    sm: downloading ? 6 : 12,
    md: downloading ? 6 : 12,
    lg: 6,
  };

  return (
    <div id='full'>
      <div style={{ margin: '8px' }}>
        <h2
          style={{
            color: 'black',
            fontWeight: '600',
          }}
          align='center'
        >
          ANSI/IES TM-30-20 Color Rendition Report
        </h2>
        <MetaDataForm showMetadataActions={showMetadataActions} />
        {addNewModalContainer}

        <Row
          style={{
            marginTop: '1rem',
          }}
          align='center'
        >
          <Col {...reportColumnWidths}>
            <CVGPlot
              numericalResults={Numerical_Results}
              active='full'
            ></CVGPlot>
          </Col>
          <Col {...reportColumnWidths} style={{ marginTop: '6rem' }}>
            <LCSPlot data={Numerical_Results.local_chroma_shift}></LCSPlot>
            <div style={{ margin: '1.5rem 0' }}></div>
            <LHSPlot
              active={active}
              data={Numerical_Results.local_hue_shift}
            ></LHSPlot>
          </Col>
        </Row>
        <Row align='center'>
          <Col {...reportColumnWidths}>
            <ELFPlot
              wavelengths={input.Wavelength}
              test={Numerical_Results.normalized_SPD_test}
              ref_spd={Numerical_Results.normalized_SPD_ref}
            ></ELFPlot>
          </Col>
          <Col {...reportColumnWidths} style={{ marginTop: '2rem' }}>
            <LCFPlot
              active={active}
              data={Numerical_Results.local_color_fidelity}
            ></LCFPlot>
          </Col>
        </Row>
        <Row align='center'>
          <Col md={12}>
            <div style={{ overflowX: downloading ? 'initial' : 'auto' }}>
              <SCFPlot data={Numerical_Results.color_sample_fidelity} />
            </div>
          </Col>
        </Row>
        {/* <Row align="center" style={{ padding: "25px 0" }}>
          Color are for visual orientation purpose only. Created with the
          ANSI/IES TM-30-18 Web Calculator
        </Row> */}
      </div>
      {window.location != window.parent.location ? (
        <div
          style={{
            display: 'flex',
            textAlign: 'left',
            marginTop: '3rem',
            fontWeight: '500',
          }}
        >
          <img
            alt='app-logo'
            style={{ width: 40, height: 40, marginLeft: 10 }}
            src={`${process.env.PUBLIC_URL}/images/ies-logo.jpg`}
          />
          <p style={{ marginLeft: 15 }}>
            The content of this report was generated by the Illuminating
            Engineering Society’s online spectral calculator (
            <a
              target='_blank'
              rel='noopener noreferrer'
              href='https://www.ies.org/spectral-calculator'
            >
              www.ies.org/spectralcalculator
            </a>
            ), in conformance with the ANSI/IES {downloading && <br />}
            TM-30-20 standard published by the IES, 120 Wall Street, New York,
            New York 10005.
          </p>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '3rem',
            fontWeight: '500',
          }}
        >
          <p>PNNL Spectral Calculator</p>
          {/* <p>Version 2.03</p> */}
          <p>Created {moment().format('l')}</p>
        </div>
      )}
    </div>
  );
};

Full.propTypes = {
  Numerical_Results: PropTypes.object,
  input: PropTypes.object,
  showMetadataActions: PropTypes.bool,
  active: PropTypes.string,
};
